import React, { useCallback } from 'react';
import isEqual from 'lodash.isequal';

import { routes } from 'config';
import { createPath, createPathId, getProductCDNUrl } from 'modules/shared/utils';
import { useRouter } from 'modules/shared/hooks';
import { ClickableContainer, LoadableImage } from 'modules/shared-ui/components';

import { ProductQuantitySelector } from './components';
import { ProductThumbnailProps } from './product-thumbnail-type';

import enhancer from './product-thumbnail-enhancer';

import styles from './product-thumbnail-component.module.css';

export const InternalProductThumbnail: React.FC<ProductThumbnailProps> = React.memo(
    ({
        product,
        hideBrandName,
        hideProductName,
        hidePrice,
        centerLabels,
        currentListElement,
        updateQuantity,
        isAuthenticated,
    }: ProductThumbnailProps) => {
        const { history, location } = useRouter();
        const handleQuantityChange = useCallback(
            newQuantity => {
                if (isAuthenticated) {
                    updateQuantity(product.variantId, newQuantity);
                } else {
                    history.push(`${routes.espacePerso.login}?redirectPath=${location.pathname}`);
                }
            },
            [product, updateQuantity, isAuthenticated, history, location],
        );

        const quantity = currentListElement ? currentListElement.quantity : 0;
        const reservedQuantity = currentListElement ? currentListElement.reservedQuantity : 0;

        return (
            <div className={`${styles.container} ${quantity > 0 ? styles.added : ''}`}>
                {quantity > 0 && <div className={styles.addedIcon} />}
                <ClickableContainer
                    className={styles.clickableContainer}
                    link={`${routes.catalogue.product}/${createPathId(createPath(product.name), product.id)}`}
                >
                    <div className={styles.box}>
                        {quantity > 0 && (
                            <div className={styles.addedMsg}>
                                AJOUTÉ
                                <br />A MA LISTE
                            </div>
                        )}
                        <picture>
                            <LoadableImage
                                className={styles.productImg}
                                placeholderClassName={styles.imagePlaceholder}
                                alt={`${product.brand} - ${product.name}`}
                                src={getProductCDNUrl(product.fileName)}
                            />
                        </picture>
                    </div>
                </ClickableContainer>
                <div className={styles.productInfos}>
                    <div className={styles.productDesc}>
                        {!hideBrandName ? (
                            <h5 className={`${styles.brand} ${centerLabels ? styles.center : ''}`}>{product.brand}</h5>
                        ) : (
                            <h5 className={styles.brand}>&nbsp;</h5>
                        )}
                        {!hideProductName && (
                            <h5 className={`${styles.label} ${centerLabels ? styles.center : ''}`}>{product.name}</h5>
                        )}
                    </div>
                    {!hidePrice && (
                        <div className={styles.productPrice}>
                            <h5 className={styles.priceValue}>{product.price} €</h5>
                            {product.variantId && (
                                <div className={styles.manageProductContainer}>
                                    <ProductQuantitySelector
                                        quantity={quantity}
                                        onQuantityChange={handleQuantityChange}
                                        minQuantity={reservedQuantity}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    },
    (prevProps, nextProps) => {
        return isEqual(prevProps, nextProps);
    },
);

export const ProductThumbnail = enhancer(InternalProductThumbnail);
